import React, {
  ChangeEventHandler,
  KeyboardEventHandler,
  FocusEventHandler,
  FocusEvent,
} from 'react';
import { compose, defaultProps } from 'recompose';
import Textarea from './Textarea';
import Input from './Input/Input';

type HTMLElementTypes = HTMLInputElement | HTMLTextAreaElement;

type InputType = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLElementTypes>,
  HTMLElementTypes
>;

export type TextInputProps = Partial<Omit<InputType, 'ref'>> & {
  value?: string | undefined;
  onChange?: ChangeEventHandler<HTMLElementTypes>;
  onBlur?: (e?: FocusEvent<HTMLElementTypes>) => any;
  onFocus?: FocusEventHandler<HTMLElementTypes>;
  onKeyDown?: KeyboardEventHandler<HTMLElementTypes>;
  inputRef?: (ref: HTMLElementTypes) => any;
  multiline?: boolean;
  disabled?: boolean;
  style?: { [key: string]: any };
  autosize?: boolean;
  className?: string;
  error?: boolean;
  warning?: boolean;
  'data-testid'?: string;
};

const noop = () => {};

const defaultPropsObject = {
  onBlur: noop,
  onFocus: noop,
  onKeyDown: noop,
  inputRef: noop,
  multiline: false,
  autosize: true,
  disabled: false,
  style: {},
};

const enhance = compose<{}, TextInputProps>(defaultProps(defaultPropsObject));

const getStyles = (
  error: boolean | undefined,
  warning: boolean | undefined,
  disabled: boolean,
  style = {},
) => {
  const inputStyles = {
    width: '100%',
    height: '100%',
    backgroundColor: disabled ? 'inherit' : 'white',
    ...style,
  };

  if (warning) {
    return {
      ...inputStyles,
      border: '1px solid #FAAD14',
    };
  }

  if (error) {
    return {
      ...inputStyles,
      border: '1px solid #FF344A',
    };
  }

  return inputStyles;
};

const TextInput: React.StatelessComponent<TextInputProps> = propsOutter => {
  const { multiline, style, error, warning, disabled, autosize, ...props } =
    propsOutter;
  const inputStyle = getStyles(error, warning, disabled as boolean, style);

  if (multiline) {
    return autosize ? (
      <Textarea disabled={disabled} style={inputStyle} {...props} />
    ) : (
      <Input
        disabled={disabled}
        style={inputStyle}
        {...props}
        type="textarea"
      />
    );
  }

  return <Input disabled={disabled} style={inputStyle} {...props} />;
};

export default enhance(TextInput);
